import React, { useEffect, useState } from 'react';
import { Modal, Select, Typography, Upload, Icon, Divider, Input } from 'antd';
import styled from 'styled-components';
import Papa from 'papaparse';
import uuid from 'uuid';

import { AntButton as Button } from 'components/molecules/Button';
import { useUser } from 'contexts/user';
import AddStudent from 'pages/Admin/CreateCourse/RegisterStudents/AddStudent';
import EditStudent from 'pages/Admin/CreateCourse/RegisterStudents/EditStudent';
import { DownloadFileFromURL } from 'utils/helper';
import userRequests from 'services/user';
import adminRequests from 'services/admin';
import courseRequests from 'services/course';
import accountRequest from 'services/account';
import Table from './Table';
import { uniqueId } from 'lodash';
import AddInstructor from '../CreateCourse/InviteInstructor/AddInstructor';

const generator = require('generate-password');

const { Option } = Select;
const { Text, Title } = Typography;
const { warning } = Modal;

const FormContainer = styled.div`
    height: 100%;
    background-color: #fafafa;
    display: flex;
    flex-direction: column;
    padding: 12px 36px 24px;
`;

const SectionWrapper = styled.div`
    overflow: auto;
`;

const TableWrapper = styled.div`
    flex: 1;
    padding: 12px;
`;

const FormItemTitle = styled(Text)`
    margin-right: 10px;
`;

const Form = () => {
    const { findAllCourses } = useUser();

    const [rowData, setRowData] = useState([]);
    const [semesterSelect, setSemesterSelect] = useState();
    const [courseSelect, setCourseSelect] = useState();
    const [courses, setCourses] = useState([]);

    const [addModal, setAddModal] = useState(false);
    const [addInstructorModal, setAddInstructorModal] = useState(false);
    const [editModal, setEditModal] = useState(false);

    useEffect(() => {
        const getCourses = async () => {
            const coursesReq = await adminRequests.listAllCourses();
            setCourses(coursesReq.courses);
        };
        getCourses();
    }, []);

    const parseSchool = (data = []) => {
        return [...new Set(data.map((item) => item.school))];
    };

    const parseSemester = (data) => {
        return [...new Set(data.map((item) => item.semester))];
    };

    const parseCourse = (data) => {
        return data.map(({ name, section, id, semester, school }) => ({
            id,
            label: `${name} ${section}`,
            semester,
            school,
        }));
    };

    const createStudentRecord = async (data) => {
        const { groupNumber, name, studentId } = data;

        const payload = {
            course_id: courseSelect,
            students: [
                {
                    group: groupNumber,
                    name,
                    password: generator.generate({
                        length: 10,
                        numbers: true,
                    }),
                    id: uuid.v4(),
                    studentId,
                    username: studentId,
                },
            ],
        };

        // const res = await postRequest(ADMIN.ADD_STUDENTS, payload);
        const res = await adminRequests.createStudents(payload);
        const { user, group, account } = res[0];

        return {
            id: account.id,
            active: account.active,
            studentId: user.studentId,
            username: user.username,
            groupNumber: group.number,
            password: user.password,
        };
    };

    const createInstructorRecord = async (data) => {
        const { username, password, name } = data;
        const payload = {
            course_id: courseSelect,
            instructors: [
                {
                    name: name,
                    first_name: name,
                    last_name: '',
                    password,
                    username: username,
                },
            ],
        };
        const res = await adminRequests.createInstructors(payload);
    };

    const schoolOptions = parseSchool(courses).map((val) => <Option key={val}>{val}</Option>);

    const semesterOptions = parseSemester(courses).map((val) => <Option key={val}>{val}</Option>);

    const courseOptions = parseCourse(courses)
        .filter(({ semester }) => semester === semesterSelect)
        .map(({ id, label }) => <Option key={id}>{label}</Option>);

    const handleChangeSemester = (selected) => {
        setSemesterSelect(selected);
    };

    const handleChangeCourse = async (selected) => {
        setCourseSelect(selected);
        const data = await adminRequests.listStudents({ course_id: selected });
        if (!data.length) {
            return;
        }

        const parsedData = data.map((item) => ({
            id: item.account_id,
            active: item.active,
            groupNumber: item.groupNumber,
            userId: item.user_id,
            studentId: item.studentId,
            username: item.username,
            password: item.password,
            mutable: false,
        }));

        setRowData(parsedData);
    };

    const handleClickDownloadRows = () => {
        const csvText = Papa.unparse(
            rowData.map((item) => {
                const { studentId, username, groupNumber, password } = item;
                return {
                    'Student ID': studentId,
                    Username: username,
                    Group: groupNumber,
                    Password: password,
                };
            }),
            { header: true }
        );

        const blob = new Blob([csvText], {
            type: 'text/plain;charset=utf-8',
        });
        const downloadUrl = URL.createObjectURL(blob);
        DownloadFileFromURL(downloadUrl, 'accounts.csv');
    };

    const handleAddRow = () => {
        if (!courseSelect) {
            warning({
                title: 'Unable to proceed',
                content: 'Please select course before adding new account.',
            });
            return;
        }

        setAddModal(true);
    };

    const handleAddInstructorRow = () => {
        if (!courseSelect) {
            warning({
                title: 'Unable to proceed',
                content: 'Please select course before adding new account.',
            });
            return;
        }

        setAddInstructorModal(true);
    };

    const handleSubmitAdd = async (data) => {
        const { studentId, name, group: groupNumber } = data;
        const response = await createStudentRecord({ studentId, name, groupNumber });
        const totalList = [].concat(response, rowData);
        const reducedList = totalList.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
        setRowData(reducedList);
        setAddModal(false);
    };

    const handleSubmitAddInstructor = async (data) => {
        const response = await createInstructorRecord(data);
        setAddInstructorModal(false);
    };

    const handleSubmitEdit = () => {
        setRowData(
            [].concat(
                {
                    'Student ID': '',
                    Password: generator.generate({
                        length: 10,
                        numbers: true,
                    }),
                },
                rowData
            )
        );
    };

    const handleSubmitUserData = async ({ id, key, value }) => {
        const { user_id } = rowData.find((item) => item.id === id);
        const user = await userRequests.updateUser(user_id, { [key]: value });
        return user;
    };

    const handleCheckDuplicate = async (data) => {
        // const res = await postRequest(ADMIN.LIST_USERS, data);
        const res = await adminRequests.listUsers(data);
        if (res && res.length) return false;
        return true;
    };

    const activateStudent = (accountId) => {
        return adminRequests.studentActivate({ account_id: accountId });
    };

    const deactivateStudent = (accountId) => {
        return adminRequests.studentDeactivate({ account_id: accountId });
    };

    const handleClickRemove = async (data) => {
        const { id, active } = data;

        const toggleStatus = () => {
            setRowData(
                rowData.map((item) => {
                    if (item.id === id) return { ...item, active: !item.active };
                    return item;
                })
            );
        };

        toggleStatus();

        if (active) {
            deactivateStudent(data.id).catch((err) => {
                console.log(err);
                toggleStatus();
            });
            return;
        }

        activateStudent(data.id).catch((err) => {
            toggleStatus();
        });
    };

    const handleSubmitGroupChange = async ({ id, value }) => {
        const user = rowData.find((item) => item.id === id);
        await deactivateStudent(id);
        const response = await createStudentRecord({
            groupNumber: value,
            studentId: user.studentId,
        });

        setRowData(
            [].concat(
                response,
                rowData.map((item) => {
                    if (item.id === id) return { ...item, active: false };
                    return item;
                })
            )
        );
    };

    const columns = [
        {
            key: 'studentId',
            name: 'Student ID',
            disabled: true,
            validate: {
                type: 'action',
                action: ({ value }) => handleCheckDuplicate({ studentId: value }),
                onError: () =>
                    warning({
                        title: 'Unable to proceed',
                        content: 'The provided Student ID duplicates with another user',
                    }),
            },
            onSubmit: handleSubmitUserData,
        },
        {
            key: 'username',
            name: 'Username',
            disabled: true,
            validate: {
                type: 'action',
                action: ({ value }) => handleCheckDuplicate({ username: value }),
                onError: () =>
                    warning({
                        title: 'Unable to proceed',
                        content: 'The provided Username duplicates with another user',
                    }),
            },
            onSubmit: handleSubmitUserData,
        },
        {
            key: 'groupNumber',
            name: 'Group',
            disabled: true,
            // validate: {
            //   type: 'regex',
            //   regex: /^[0-9]+$/,
            //   onError: () =>
            //     warning({
            //       title: 'Unable to proceed',
            //       content: 'Please provide numbers only',
            //     }),
            // },
            // onSubmit: handleSubmitGroupChange,
        },
        {
            key: 'password',
            name: 'Password',
            disabled: true,
            validate: {
                type: 'regex',
                regex: /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])([a-zA-Z0-9]{8,})$/g,
                onError: () =>
                    warning({
                        title: 'Unable to proceed',
                        content: (
                            <>
                                <li>At least 8 characters</li>
                                <li>At least one uppercase letter</li>
                                <li>At least one lowercase letter</li>
                                <li>At least one number</li>
                            </>
                        ),
                    }),
            },
            onSubmit: handleSubmitUserData,
        },
        {
            key: 'active',
            name: 'Status',
            content() {
                return <div>{this.active ? 'Active' : 'Inactive'}</div>;
            },
        },
        {
            key: 'action',
            name: 'Action',
            content() {
                return (
                    <div style={{ color: '#6347FF', cursor: 'pointer' }} onClick={() => handleClickRemove(this)}>
                        {this.active ? 'Deactivate' : 'Activate'}
                    </div>
                );
            },
        },
    ];

    return (
        <FormContainer>
            <SectionWrapper>
                <div style={{ float: 'left' }}>
                    <div style={{ display: 'inline-block', padding: '12px' }}>
                        <FormItemTitle>School:</FormItemTitle>
                        <Select
                            disabled
                            style={{ width: 160 }}
                            showSearch
                            placeholder='Select a school'
                            optionFilterProp='children'
                        >
                            {schoolOptions}
                        </Select>
                    </div>
                    <div style={{ display: 'inline-block', padding: '12px' }}>
                        <FormItemTitle>Semester:</FormItemTitle>
                        <Select
                            showSearch
                            style={{ width: 160 }}
                            placeholder='Select a semester'
                            optionFilterProp='children'
                            onChange={handleChangeSemester}
                        >
                            {semesterOptions}
                        </Select>
                    </div>
                    <div style={{ display: 'inline-block', padding: '12px' }}>
                        <FormItemTitle>Course:</FormItemTitle>
                        <Select
                            showSearch
                            style={{ width: 160 }}
                            placeholder='Select a course'
                            optionFilterProp='children'
                            onChange={handleChangeCourse}
                        >
                            {courseOptions}
                        </Select>
                    </div>
                </div>
                <div style={{ float: 'right', padding: '12px' }}>
                    <Button onClick={handleAddInstructorRow} style={{ width: '175px' }} icon='plus'>
                        NEW INSTRUCTOR
                    </Button>
                </div>
                <div style={{ float: 'right', padding: '12px' }}>
                    <Button onClick={handleAddRow} style={{ width: '150px' }} icon='plus'>
                        NEW STUDENT
                    </Button>
                </div>
                <div style={{ float: 'right', padding: '12px' }}>
                    <Button onClick={handleClickDownloadRows} style={{ width: '150px' }}>
                        DOWNLOAD CSV
                    </Button>
                </div>
            </SectionWrapper>
            <TableWrapper>
                <Table columns={columns} rows={rowData} />
            </TableWrapper>
            <Modal key={Math.random()} centered visible={addModal} onCancel={() => setAddModal(false)} footer={null}>
                <AddStudent onAdd={handleSubmitAdd} onCancel={() => setAddModal(false)} />
            </Modal>
            <Modal
                key={Math.random()}
                centered
                visible={addInstructorModal}
                onCancel={() => setAddInstructorModal(false)}
                footer={null}
            >
                <AddInstructor onSave={handleSubmitAddInstructor} onCancel={() => setAddInstructorModal(false)} />
            </Modal>
            <Modal key={Math.random()} centered visible={editModal} onCancel={() => setEditModal(false)} footer={null}>
                <EditStudent onSave={handleSubmitEdit} onCancel={() => setEditModal(false)} />
            </Modal>
        </FormContainer>
    );
};

export default Form;
